/**
 * Mapeia cada template a um ícone Lucide único + label curto.
 *
 * As chaves batem com `issue_templates.key` no backend (formato snake_case).
 * Quando um novo template é adicionado em server/src/seeds/nd-templates.ts,
 * adicione aqui um entry com ícone semanticamente diferente dos outros pra
 * o user distinguir cada card no Quadro/NovaTarefa.
 */
import {
  Activity,
  BarChart3,
  BookOpen,
  Box,
  Calendar,
  Check,
  Clapperboard,
  ClipboardCheck,
  DollarSign,
  Eye,
  FileBarChart,
  FileBarChart2,
  FileText,
  Film,
  Flag,
  GalleryHorizontal,
  GalleryThumbnails,
  GitBranch,
  GitCompare,
  Globe,
  GraduationCap,
  Headphones,
  Image as ImageIcon,
  Layers,
  Lightbulb,
  Mail,
  Map,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Mic,
  Mic2,
  MousePointer,
  Music,
  Package,
  Palette,
  PenLine,
  Presentation,
  RefreshCw,
  Rocket,
  Scale,
  Scissors,
  ScrollText,
  Search,
  Send,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Target,
  TrendingUp,
  Tv,
  Video,
  Wand2,
  Workflow,
  type LucideIcon,
} from "lucide-react";

/**
 * Profissionais ("agentes") da equipe ND. Cada passo do fluxo é executado por
 * um deles — o detalhe do card mostra "feito por [agente]" pra comunicar que
 * uma tarefa passa por VÁRIOS profissionais (ex: post = social media escreve →
 * você revisa → diretor de arte ilustra). `voce` representa o próprio usuário
 * nos passos de revisão/aprovação.
 */
export type AgentKey =
  | "social_media"
  | "copywriter"
  | "strategist"
  | "traffic"
  | "market_analyst"
  | "art_director"
  | "voce";

export const AGENTS: Record<AgentKey, { name: string; icon: LucideIcon }> = {
  social_media: { name: "Social Media", icon: Megaphone },
  copywriter: { name: "Copywriter", icon: PenLine },
  strategist: { name: "Estrategista", icon: Lightbulb },
  traffic: { name: "Especialista em Tráfego", icon: BarChart3 },
  market_analyst: { name: "Analista de Mercado", icon: Search },
  art_director: { name: "Diretor de Arte", icon: Palette },
  voce: { name: "Você", icon: Eye },
};

/** Mapeia a categoria do template no agente que normalmente a executa —
 *  usado pra auto-derivar o agente quando o passo não define um explicitamente. */
function agentForCategory(category?: string): AgentKey {
  switch (category) {
    case "Anúncios":
      return "traffic";
    case "Conteúdo":
      return "social_media";
    case "E-mail":
    case "Web":
    case "Automação":
      return "copywriter";
    case "Estratégia":
      return "strategist";
    case "Pesquisa":
      return "market_analyst";
    default:
      return "copywriter";
  }
}

export interface FlowStep {
  /** Ícone Lucide do passo */
  icon: LucideIcon;
  /** Label curto: "Roteiro dos slides", "Você revisa", "Arte das imagens" */
  label: string;
  /** Descrição auxiliar abaixo do label, opcional */
  desc?: string;
  /** Profissional que executa esse passo. Mostrado como "feito por X" no
   *  detalhe do card. Passos de revisão usam `voce`. */
  agent?: AgentKey;
}

export interface TemplateMeta {
  /** Label curto pra badge/pill. */
  label: string;
  /** Ícone Lucide único do template. */
  icon: LucideIcon;
  /** Tagline curta de marketing pro card. */
  tagline?: string;
  /** Descrição amigável pro user — substitui a description técnica. */
  userDescription?: string;
  /** Estimativa de tempo casual. */
  eta?: string;
  /**
   * Fluxo lúdico de 2-5 passos mostrado no modal antes do user confirmar
   * que quer criar a tarefa. Não-técnico — comunica O QUE VAI ACONTECER.
   * Auto-derivado se não definido (1 passo "Geramos {tagline}" + "Você revisa").
   */
  flow?: FlowStep[];
  /**
   * Fonte de dado externa que alimenta o card (ex: "google_ads", "meta_ads").
   * Renderiza o selo da marca no canto do ícone, pro user entender de onde vem
   * o dado. Usa o mesmo logo de `/integrations/{source}.png`.
   */
  source?: string;
}

const FALLBACK: TemplateMeta = { label: "Tarefa", icon: Sparkles };

/**
 * Mapping completo dos 58 templates. Mantenha os ícones únicos por phase
 * pra que cards lado a lado fiquem visualmente distinguíveis.
 */
export const TEMPLATE_META: Record<string, TemplateMeta> = {
  // ── Análise & Estratégia (disponíveis) ──
  analise_meta_ads: {
    label: "Meta Ads",
    icon: BarChart3,
    source: "meta_ads",
    tagline: "Diagnóstico das suas campanhas no Meta",
    userDescription:
      "Olha os números reais das suas campanhas no Facebook e Instagram. Mostra o que está dando resultado, o que está caro demais e quais ajustes fazer pra essa semana.",
    eta: "~10 min",
    flow: [
      { icon: Activity, label: "Buscamos os dados", desc: "Últimos 30 dias das suas campanhas", agent: "traffic" },
      { icon: BarChart3, label: "Analisamos os números", desc: "Custo, conversão e desempenho de cada campanha", agent: "traffic" },
      { icon: ClipboardCheck, label: "Plano de 3-5 ações", desc: "Priorizadas por impacto, com o número que justifica cada decisão", agent: "traffic" },
      { icon: Eye, label: "Você revisa e aplica", agent: "voce" },
    ],
  },
  analise_google_ads: {
    label: "Google Ads",
    icon: TrendingUp,
    source: "google_ads",
    tagline: "Diagnóstico das suas campanhas no Google",
    userDescription:
      "Análise das suas campanhas no Google Ads. Identifica o que está performando, onde está gastando demais e onde mexer pra reduzir custo e ganhar conversão.",
    eta: "~10 min",
  },
  briefing_criativo: {
    label: "Briefing",
    icon: Lightbulb,
    tagline: "Especificação completa pra um anúncio novo",
    userDescription:
      "Estrutura pronta pra produzir um anúncio: público-alvo, ângulo da mensagem, sugestão de copy e visual, variações pra teste e métricas pra acompanhar. Vira o documento que orienta o time de design ou seu próprio editor.",
    eta: "~5 min",
  },
  calendario_editorial: {
    label: "Calendário",
    icon: Calendar,
    tagline: "Plano de conteúdo do mês",
    userDescription:
      "Plano completo do que postar a cada dia do mês: tema, formato (post, reels, story) e o objetivo de cada peça. Construído ao redor das suas prioridades atuais no método ESCALA.",
    eta: "~8 min",
  },
  pesquisa_concorrentes: {
    label: "Descobrir concorrentes",
    icon: Search,
    tagline: "Lista de perfis pra você analisar depois",
    userDescription:
      "Use quando você ainda não sabe quem são seus concorrentes. Recebe uma lista organizada de perfis do Instagram (com o @ e o porquê de cada um ser concorrente): os diretos, os parecidos e referências boas pra se inspirar. Depois, pra cada um, você pode criar uma base de concorrente pra estudar a fundo.",
    eta: "~6 min",
    flow: [
      {
        icon: Search,
        label: "Busca na internet",
        desc: "Procura os perfis que mais aparecem e estão ativos no seu nicho",
        agent: "market_analyst",
      },
      {
        icon: Layers,
        label: "Separa em 3 grupos",
        desc: "Concorrentes diretos, parecidos e referências pra se inspirar",
        agent: "market_analyst",
      },
      {
        icon: Eye,
        label: "Você revisa e aprova",
        agent: "voce",
      },
      {
        icon: BookOpen,
        label: "Vira base pra análise profunda",
        desc: "Pra cada perfil escolhido, você cria uma base de concorrente pra estudar a fundo",
        agent: "voce",
      },
    ],
  },

  // ── Análise & Estratégia (em breve) ──
  pesquisa_tendencias: {
    label: "Tendências",
    icon: TrendingUp,
    tagline: "Temas em alta no seu nicho",
    userDescription: "Levantamento dos assuntos que estão bombando no seu nicho agora. Útil pra entrar na conversa antes do mercado saturar.",
  },
  pesquisa_papers: {
    label: "Papers",
    icon: GraduationCap,
    tagline: "Evidência científica pra autoridade",
    userDescription: "Pesquisa em estudos científicos pra dar respaldo ao seu posicionamento. Útil pra médico, advogado ou qualquer profissional que precisa citar fonte confiável.",
  },
  analise_regulacao: {
    label: "Regulação",
    icon: Scale,
    tagline: "O que pode e o que não pode dizer",
    userDescription: "Mapeia as regras do seu conselho de classe ou órgão regulador (CFM, OAB, CONAR). Evita problemas e mostra os ângulos seguros pra usar na comunicação.",
  },
  big_idea: {
    label: "Big Idea",
    icon: Sparkles,
    tagline: "O conceito que diferencia a sua oferta",
    userDescription:
      "O conceito-mãe que faz sua oferta se destacar do resto do mercado. Define a promessa central, o mecanismo único (o COMO funciona) e o ângulo de venda. Vira a base de toda a comunicação da campanha.",
    eta: "~10 min",
    flow: [
      { icon: Lightbulb, label: "Análise da oferta", desc: "Briefing + concorrência", agent: "strategist" },
      { icon: PenLine, label: "Conceito + mecanismo único", desc: "A grande sacada + como ela funciona na prática", agent: "strategist" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  criar_produto: {
    label: "Novo produto",
    icon: Package,
    tagline: "Concepção de um produto do zero",
    userDescription: "Estruturação de um produto novo a partir do perfil do seu público. Define formato, escopo, preço sugerido e a oferta principal.",
  },
  esteira_produtos: {
    label: "Esteira de produtos",
    icon: Layers,
    tagline: "Sequência de produtos que se complementam",
    userDescription: "Mapeia uma sequência de produtos do mais barato (porta de entrada) ao mais caro (alto valor). Aumenta o ticket médio e a recorrência do mesmo cliente.",
  },
  mapa_jornada: {
    label: "Jornada do cliente",
    icon: Map,
    tagline: "O caminho que o cliente percorre",
    userDescription: "Mapeia o caminho do cliente desde o primeiro contato até a compra. Mostra em que ponto ele costuma travar e o que cada etapa precisa entregar.",
  },
  pricing_strategy: {
    label: "Pricing",
    icon: DollarSign,
    tagline: "Quanto cobrar pelo seu produto",
    userDescription: "Análise de quanto cobrar baseada no valor entregue, no seu posicionamento e nas referências do mercado. Sai com preço sugerido e a justificativa.",
  },
  posicionamento_marca: {
    label: "Posicionamento",
    icon: Target,
    tagline: "Quem você é pro mercado",
    userDescription: "Define quem você é pro mercado, com quem você fala e contra quem se posiciona. Vira a base pra toda comunicação soar consistente.",
  },
  plano_lancamento: {
    label: "Plano de lançamento",
    icon: Rocket,
    tagline: "Estrutura completa do lançamento",
    userDescription: "Plano completo do lançamento em três fases: pré-aquecimento, evento principal e pós-evento. Inclui cronograma, conteúdos por fase e metas em cada etapa.",
  },
  analise_hotmart: {
    label: "Hotmart",
    icon: ShoppingBag,
    tagline: "Diagnóstico das suas vendas",
    userDescription: "Análise das suas vendas na Hotmart: o que está vendendo, taxa de conversão, perfil dos compradores e onde tem dinheiro deixado na mesa.",
  },
  analise_clarity: {
    label: "Clarity",
    icon: MousePointer,
    tagline: "Comportamento real dos visitantes",
    userDescription: "Análise do que as pessoas fazem no seu site: onde clicam, onde travam, onde desistem. Útil pra otimizar landing pages e checkout.",
  },
  analise_rd_station_marketing: {
    label: "RD Station",
    icon: Send,
    tagline: "Performance das suas automações",
    userDescription: "Diagnóstico das suas automações no RD Station. Quais e-mails estão sendo abertos, quais convertem em vendas e o que precisa ser ajustado.",
  },

  // ── Copy & Roteiro (disponíveis) ──
  post_instagram: {
    label: "Post",
    icon: ImageIcon,
    tagline: "Pronto pra postar hoje",
    userDescription:
      "Texto do post pronto pra publicar: hook que prende, corpo, chamada e hashtags. Você aprova a copy e a gente gera a arte logo em seguida.",
    eta: "~5 min",
    flow: [
      { icon: PenLine, label: "Copy do post", desc: "Hook, legenda, hashtags", agent: "social_media" },
      { icon: Eye, label: "Você revisa", desc: "Aprova, ajusta ou reprova", agent: "voce" },
      { icon: Palette, label: "Arte da imagem", desc: "1 imagem quadrada alinhada com a marca", agent: "art_director" },
      { icon: Check, label: "Pronto pra postar", agent: "voce" },
    ],
  },
  carrossel: {
    label: "Carrossel",
    icon: GalleryHorizontal,
    tagline: "Conteúdo que segura quem está rolando",
    userDescription:
      "Roteiro completo do carrossel: o que vai em cada slide (texto e descrição visual) e a legenda final do post. Depois você aprova e a gente gera a arte de cada slide.",
    eta: "~8 min",
    flow: [
      {
        icon: PenLine,
        label: "Roteiro dos slides",
        desc: "Hook, conteúdo, CTA e legenda completa do post",
        agent: "social_media",
      },
      {
        icon: Eye,
        label: "Você revisa",
        desc: "Aprova, ajusta ou reprova antes de virar arte",
        agent: "voce",
      },
      {
        icon: Palette,
        label: "Arte das imagens",
        desc: "1 imagem por slide, paleta e estilo consistentes",
        agent: "art_director",
      },
      {
        icon: Check,
        label: "Pronto pra postar",
        desc: "Você baixa as imagens e copia a legenda",
        agent: "voce",
      },
    ],
  },
  stories: {
    label: "Stories",
    icon: Smartphone,
    tagline: "Sequência de stories pra hoje",
    userDescription:
      "Sequência completa de stories pra publicar hoje: cada um com texto sobreposto, ideia visual e tipo de interação (enquete, caixa de pergunta, link). Depois geramos as artes verticais.",
    eta: "~5 min",
    flow: [
      { icon: PenLine, label: "Roteiro dos stories", desc: "Texto + descrição visual de cada um", agent: "social_media" },
      { icon: Eye, label: "Você revisa", agent: "voce" },
      { icon: Palette, label: "Arte vertical", desc: "Imagens com espaço pra você pôr enquete, link e figurinha por cima", agent: "art_director" },
      { icon: Check, label: "Pronto pra subir", agent: "voce" },
    ],
  },
  reels: {
    label: "Reels",
    icon: Film,
    tagline: "Roteiro de Reels que prende",
    userDescription:
      "Roteiro completo do Reels marcado tempo a tempo: o que falar, o que mostrar e quando virar a câmera. Pronto pra você gravar.",
    eta: "~6 min",
  },
  roteiro_vsl: {
    label: "VSL",
    icon: Tv,
    tagline: "Roteiro do vídeo de vendas",
    userDescription:
      "Roteiro completo do seu vídeo de vendas: abertura que prende, apresentação da dor, virada com a sua solução, prova social, oferta e fechamento. Marcado tempo a tempo, pronto pra gravar.",
    eta: "~10 min",
    flow: [
      { icon: PenLine, label: "Roteiro do vídeo", desc: "Abertura · dor · solução · prova · oferta · CTA, marcado tempo a tempo", agent: "copywriter" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  email_nutricao: {
    label: "E-mail",
    icon: Mail,
    tagline: "E-mail que educa e converte",
    userDescription:
      "E-mail pra educar o lead e mover ele pro próximo passo do funil. Inclui 3 variações de assunto, o texto de prévia, o corpo do e-mail e a chamada pra ação.",
    eta: "~7 min",
  },
  copy_landing: {
    label: "Landing",
    icon: Globe,
    tagline: "Texto completo da página de conversão",
    userDescription:
      "Texto completo da landing page: headline principal, subtítulo, lista de benefícios, prova social, oferta, FAQ e chamada pra ação. Pronto pra entregar pro designer ou colocar direto no construtor de página.",
    eta: "~15 min",
  },
  fluxo_manychat: {
    label: "ManyChat",
    icon: MessageCircle,
    tagline: "Mensagens automáticas no Instagram",
    userDescription:
      "Sequência de mensagens automáticas no Instagram (DM ou resposta de story). Define o gatilho, as mensagens, as ramificações por resposta e onde o lead vai cair no final.",
    eta: "~10 min",
  },
  sequencia_active_campaign: {
    label: "ActiveCampaign",
    icon: Workflow,
    tagline: "Sequência automática de e-mails",
    userDescription:
      "Sequência de e-mails programados pra rodar sozinhos. Cada e-mail com dia certo de envio, assunto, corpo e regras (se clicou aqui, vai pra X; se ignorou, recebe Y).",
    eta: "~12 min",
  },
  automacao_rd_station: {
    label: "RD Station",
    icon: Workflow,
    tagline: "Fluxo completo no RD Station",
    userDescription:
      "Fluxo completo de automação no RD Station: regra de entrada, sequência de e-mails, tags aplicadas a cada interação e qualificação do lead pra entregar pro time comercial.",
    eta: "~12 min",
  },

  // ── Copy & Roteiro (em breve) ──
  roteiro_aula: {
    label: "Aula",
    icon: Presentation,
    tagline: "Roteiro de aula ou live",
    userDescription: "Estrutura completa de uma aula online: abertura, ensino do conteúdo principal, exemplos práticos e chamada pra próxima etapa. Pronto pra dar ao vivo ou gravar.",
  },
  roteiro_podcast: {
    label: "Podcast",
    icon: Mic,
    tagline: "Pauta + perguntas",
    userDescription:
      "Pauta completa em 4 blocos: hook, origem, método/insights, aplicação prática, provocação final. Inclui perguntas-puxa-caixa.",
    eta: "~7 min",
    flow: [
      { icon: PenLine, label: "Pauta estruturada em blocos", desc: "Hook · Origem · Método · Aplicação · Provocação", agent: "copywriter" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  webinar_completo: {
    label: "Webinar",
    icon: Video,
    tagline: "Pacote completo do webinar",
    userDescription: "Pacote do webinar gratuito: roteiro do que falar, sugestão de slides e e-mail de inscrição. Pronto pra rodar ao vivo.",
  },
  sequencia_lancamento: {
    label: "Lançamento (e-mail)",
    icon: Send,
    tagline: "Sequência de e-mails do lançamento",
    userDescription: "Sequência de 7-15 e-mails da fase de pré-lançamento até a abertura das vagas. Cada e-mail com assunto, corpo e timing dentro do funil.",
  },
  recuperacao_carrinho: {
    label: "Carrinho abandonado",
    icon: ShoppingCart,
    tagline: "Recupera quem desistiu antes de comprar",
    userDescription: "Sequência de 3-5 e-mails pra recuperar quem abandonou o carrinho antes de finalizar a compra. Cada um com ângulo diferente: lembrete, prova social e urgência.",
  },
  whatsapp_broadcast: {
    label: "WhatsApp",
    icon: MessageSquare,
    tagline: "Disparo em massa pelo WhatsApp",
    userDescription:
      "3 variações de mensagem pra disparar pra sua base: abertura, mensagem de pergunta pra puxar resposta e mensagem final com link. Já no formato e tom certo do WhatsApp.",
    eta: "~5 min",
    flow: [
      { icon: PenLine, label: "3 variações de mensagem", desc: "Abertura · Pergunta · CTA com link", agent: "copywriter" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  sales_letter: {
    label: "Carta de Vendas",
    icon: ScrollText,
    tagline: "Texto longo pra página de venda",
    userDescription:
      "Carta de vendas longa pra página de oferta: hook, apresentação do problema, sua solução, provas, oferta com bônus, garantia, chamada pra ação e P.S. final. Estilo clássico que converte.",
    eta: "~12 min",
    flow: [
      { icon: PenLine, label: "Carta longa estruturada", desc: "Hook · problema · solução · provas · oferta · garantia · CTA", agent: "copywriter" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  anuncio_meta_completo: {
    label: "Anúncio Meta",
    icon: Megaphone,
    tagline: "Texto pro anúncio no Meta",
    userDescription: "Variações de texto pro seu anúncio no Facebook e Instagram: títulos, texto principal e chamadas pra ação, prontas pra teste.",
  },
  anuncio_google_completo: {
    label: "Anúncio Google",
    icon: Megaphone,
    tagline: "Texto pro Google Ads",
    userDescription: "Variações de texto pra Google Ads: títulos, descrições e extensões. Inclui versões pra anúncio de busca e pra Performance Max.",
  },
  linkedin_post: {
    label: "LinkedIn",
    icon: Megaphone,
    tagline: "Post profissional pro LinkedIn",
    userDescription: "Post de LinkedIn no formato que mais gera alcance: hook que prende nas duas primeiras linhas, conteúdo de valor e fechamento que provoca comentários.",
  },
  tiktok_script: {
    label: "TikTok",
    icon: Music,
    tagline: "Roteiro de TikTok",
    userDescription: "Roteiro de TikTok pensado pra retenção: hook nos primeiros 3 segundos, ritmo, ganchos pra segurar quem está assistindo e chamada no final.",
  },

  // ── Produção visual (disponíveis) ──
  post_instagram_arte: {
    label: "Arte do post",
    icon: Palette,
    tagline: "Imagem do post pronta",
    userDescription: "Arte da imagem do post (formato quadrado), gerada com base na descrição visual aprovada na copy.",
    eta: "~3 min",
  },
  carrossel_arte: {
    label: "Arte do carrossel",
    icon: GalleryThumbnails,
    tagline: "Imagens dos slides com identidade consistente",
    userDescription: "Uma imagem por slide do carrossel, todas com a mesma paleta e estilo visual. Geradas a partir das descrições que você aprovou na copy.",
    eta: "~8 min",
  },
  stories_arte: {
    label: "Arte dos stories",
    icon: Smartphone,
    tagline: "Imagens verticais dos stories",
    userDescription: "Imagens verticais dos stories aprovados, já com espaço pra você adicionar enquete, link ou figurinha por cima.",
    eta: "~5 min",
  },

  // ── Produção visual (em breve) ──
  capa_youtube: {
    label: "Capa de YouTube",
    icon: Clapperboard,
    tagline: "Capa que faz o vídeo ser clicado",
    userDescription: "Capa de vídeo do YouTube pensada pra gerar clique: imagem grande, headline curta e contraste alto.",
  },
  capa_podcast: {
    label: "Capa do podcast",
    icon: Headphones,
    tagline: "Arte do episódio",
    userDescription: "Arte quadrada do episódio do podcast pra Spotify, Apple Podcasts e divulgação. Com número do episódio e título principal.",
  },
  mockup_produto: {
    label: "Mockup do produto",
    icon: Box,
    tagline: "Imagem realista do seu produto",
    userDescription: "Imagem realista do seu produto (livro, curso, e-book) pra usar em landing page, anúncio e e-mail. Como se fosse uma foto real do produto.",
  },
  banner_evento: {
    label: "Banner do evento",
    icon: Flag,
    tagline: "Banner principal pra divulgar o evento",
    userDescription: "Banner principal do evento pra topo da landing page, post de divulgação e e-mail. Comunica data, formato e a oferta principal.",
  },
  reel_editado_live: {
    label: "Reel editado",
    icon: Scissors,
    tagline: "Cortes verticais de uma live",
    userDescription: "Edição de Reels a partir de uma live ou aula gravada. Cortes verticais com legenda dinâmica, prontos pra publicar.",
  },
  video_ia: {
    label: "Vídeo gerado por IA",
    icon: Wand2,
    tagline: "Cena curta gerada por IA",
    userDescription: "Cena curta de vídeo gerada por inteligência artificial pra usar em anúncio ou Reels. Útil quando você não tem imagem ou vídeo gravado e precisa entregar rápido.",
  },
  voz_ia: {
    label: "Voz por IA",
    icon: Mic2,
    tagline: "Narração com voz natural",
    userDescription: "Narração em voz por IA pra usar em Reels, podcast ou vídeo de venda. Tom natural, em português, pronta pra exportar e usar.",
  },
  conceito_logo: {
    label: "Logo",
    icon: PenLine,
    tagline: "Conceitos iniciais de logo",
    userDescription: "Variações iniciais de logo pra sua marca, com paleta de cores e tipografia sugeridas pra servir de ponto de partida pro designer refinar e finalizar.",
  },
  pitch_deck: {
    label: "Apresentação",
    icon: Presentation,
    tagline: "Slides pro evento ou pitch",
    userDescription: "Apresentação completa pro evento, webinar ou reunião comercial. Slides com sequência narrativa, headlines fortes e fechamento que provoca ação.",
  },

  // ── Otimização & Resultado (todas em breve, maioria são rituais) ──
  relatorio_semanal: {
    label: "Relatório semanal",
    icon: FileBarChart,
    tagline: "Resumo da semana pro cliente",
    userDescription: "Resumo da semana pro cliente: o que aconteceu, o que performou, próximos passos. Direto, com os números que importam.",
  },
  relatorio_mensal: {
    label: "Relatório mensal",
    icon: FileBarChart2,
    tagline: "Consolidado do mês pro cliente",
    userDescription: "Relatório consolidado do mês: tudo que rodou, métricas-chave e recomendações pro mês seguinte. Pronto pra apresentar ao cliente final.",
  },
  analise_pos_campanha: {
    label: "Pós-campanha",
    icon: ClipboardCheck,
    tagline: "Análise depois que a campanha acabou",
    userDescription: "Análise feita 7 dias depois do fim da campanha. O que funcionou, o que falhou e o que vale repetir na próxima.",
  },
  ab_test_setup: {
    label: "Setup A/B test",
    icon: GitBranch,
    tagline: "Estrutura completa pra rodar um teste",
    userDescription:
      "Estrutura completa pra rodar um teste A/B com base estatística: hipótese, qual métrica medir, quantas pessoas precisam ver pra ter resultado confiável e quando parar o teste.",
    eta: "~8 min",
    flow: [
      { icon: Lightbulb, label: "Hipótese", desc: "O que você acha vs o que vai testar de verdade", agent: "traffic" },
      { icon: BarChart3, label: "Cálculo de amostra", desc: "Quantos visitantes por variação · duração estimada", agent: "traffic" },
      { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
    ],
  },
  ab_test_analise: {
    label: "Análise A/B test",
    icon: GitCompare,
    tagline: "Resultado do teste com recomendação",
    userDescription: "Análise do resultado do teste A/B: qual variação venceu, com quanta confiança estatística e o que aplicar daqui pra frente.",
  },
  feedback_kpis: {
    label: "Pulso de KPIs",
    icon: Activity,
    tagline: "Metas vs realidade da semana",
    userDescription: "Compara as metas do mês com o que está acontecendo na realidade. Sinaliza desvios pra você corrigir antes do fim do período.",
  },
  atualizar_analise_ig: {
    label: "Atualizar análise IG",
    icon: RefreshCw,
    tagline: "Reprocessa os posts mais recentes",
    userDescription: "Atualiza a análise do seu Instagram com os posts mais recentes. Mantém os agentes calibrados com o que está performando agora.",
  },

  // ── Cards novos (reunião Julio, Fase 0) — em breve ──
  analise_benchmark: {
    label: "Benchmark",
    icon: GitCompare,
    tagline: "O que os concorrentes fazem que dá certo",
    userDescription:
      "Olha os concorrentes do seu nicho e levanta os padrões que estão funcionando: os formatos, temas e ofertas que mais aparecem. Vira um norte pra você não começar do zero.",
  },
  dossie_concorrentes: {
    label: "Dossiê de concorrentes",
    icon: Layers,
    tagline: "Os pontos em comum de vários concorrentes",
    userDescription:
      "Junta tudo que você cadastrou sobre seus concorrentes e mostra o que eles têm em comum: o que todo mundo está fazendo e onde sobra espaço pra você se diferenciar.",
  },
  palavras_chave_google: {
    label: "Palavras-chave",
    icon: Globe,
    source: "google_ads",
    tagline: "O que seu público procura no Google",
    userDescription:
      "Descobre os termos que as pessoas digitam no Google quando procuram o que você oferece. Útil pra escolher temas de conteúdo e as palavras certas pros anúncios.",
  },
  roteiro_criativo_video: {
    label: "Roteiro de vídeo",
    icon: Video,
    tagline: "Roteiro pronto pra gravar o anúncio",
    userDescription:
      "O roteiro do seu anúncio em vídeo, pronto pra gravar: o que falar, o que mostrar e a ordem das cenas pra prender quem está assistindo.",
  },
  campanha_facebook_ads: {
    label: "Campanha Facebook",
    icon: Megaphone,
    tagline: "Sua campanha montada no Facebook e Instagram",
    userDescription:
      "Monta a campanha de anúncios e sobe direto pra sua conta do Facebook e Instagram, já estruturada com público, orçamento e criativos.",
  },
  campanha_google_ads: {
    label: "Campanha Google",
    icon: TrendingUp,
    tagline: "Sua campanha montada no Google",
    userDescription:
      "Monta a campanha de anúncios e sobe direto pra sua conta do Google, já com as palavras-chave, os textos e a estrutura prontas.",
  },
  mensagens_time_comercial: {
    label: "Mensagens comerciais",
    icon: MessageCircle,
    tagline: "Abertura e follow-up pro time de vendas",
    userDescription:
      "Mensagens prontas pro seu time comercial usar: a primeira abordagem com o lead e os follow-ups pra quando ele não responde.",
  },
  reativacao_base: {
    label: "Reativar base",
    icon: DollarSign,
    tagline: "Quem da sua base ainda pode comprar de novo",
    userDescription:
      "Você sobe sua lista de clientes e a gente identifica quem tem mais chance de comprar de novo, com sugestão de mensagem pra reativar pelo WhatsApp.",
  },
  lookalike_setup: {
    label: "Público semelhante",
    icon: Target,
    tagline: "Público parecido com seus melhores clientes",
    userDescription:
      "Cria um público novo parecido com seus melhores clientes, pronto pra usar nos anúncios e alcançar gente com o mesmo perfil de quem já compra de você.",
  },
  analise_email_metricas: {
    label: "Métricas de e-mail",
    icon: Mail,
    tagline: "Como seus e-mails estão performando",
    userDescription:
      "Mostra como seus disparos de e-mail estão indo: quantos abrem, quantos clicam e quantos viram venda. Aponta o que ajustar pra melhorar o resultado.",
  },
  playbook_reuniao: {
    label: "Playbook de reunião",
    icon: BookOpen,
    tagline: "Vira sua reunião num passo a passo replicável",
    userDescription:
      "Você sobe a gravação ou a transcrição de uma reunião e a gente transforma no que funcionou e no que evitar, virando um passo a passo pra sua equipe repetir.",
  },
};

export function getTemplateMeta(templateKey: string | null | undefined): TemplateMeta | null {
  if (!templateKey) return null;
  return (
    TEMPLATE_META[templateKey] ?? {
      ...FALLBACK,
      label: templateKey.replace(/_/g, " "),
      icon: FileText,
    }
  );
}

/** Sentinel pra fallback explícito (compat com chamadas antigas). */
export const TEMPLATE_FALLBACK_ICON = Sparkles;

/**
 * Auto-deriva fluxo padrão pra templates que não definem `flow` explícito.
 * Modal sempre mostra algo coerente — 2 a 4 passos com ícones Lucide.
 */
export function getTemplateFlow(
  templateKey: string,
  hasNextStep: boolean,
  category?: string,
): FlowStep[] {
  const meta = TEMPLATE_META[templateKey];
  if (meta?.flow && meta.flow.length > 0) return meta.flow;

  const ProducedIcon = meta?.icon ?? Sparkles;
  const productionLabel = meta?.label ?? "entregável";
  // Agente que executa a produção — derivado da categoria do template.
  const producer = agentForCategory(category);

  if (hasNextStep) {
    // Tem próxima etapa (geralmente _arte) — fluxo de 4 passos
    return [
      { icon: PenLine, label: `Geramos ${productionLabel.toLowerCase()}`, agent: producer },
      { icon: Eye, label: "Você revisa", desc: "Aprova ou ajusta", agent: "voce" },
      { icon: Palette, label: "Próxima etapa", desc: "Sub-tarefa criada após aprovação", agent: "art_director" },
      { icon: Check, label: "Entregável final", agent: "voce" },
    ];
  }

  // Single-step (copy ou análise sem chained subtask)
  return [
    {
      icon: ProducedIcon,
      label: `Geramos ${productionLabel.toLowerCase()}`,
      desc: meta?.userDescription,
      agent: producer,
    },
    { icon: Eye, label: "Você revisa e aprova", agent: "voce" },
  ];
}

/**
 * Cores das letras ESCALA — degradê laranja (warm/inicial) → azul (cool/final)
 * dentro da paleta ND.
 *
 * E (Entender) → laranja primário
 * S (Sistematizar) → laranja-âmbar
 * C (Captar) → laranja-coral
 * A (Ativar) → roxo-vermelho (transição)
 * L (Lucrar) → azul-acinzentado
 * A² (Acelerar) → azul-marinho
 */
export const PHASE_COLORS: Record<string, { primary: string; soft: string }> = {
  E:  { primary: "#FE7000", soft: "rgba(254,112,0,0.10)" },
  S:  { primary: "#E68442", soft: "rgba(230,132,66,0.10)" },
  C:  { primary: "#D86B5A", soft: "rgba(216,107,90,0.10)" },
  A:  { primary: "#A06FA8", soft: "rgba(160,111,168,0.12)" },
  L:  { primary: "#6B7CA8", soft: "rgba(107,124,168,0.12)" },
  A2: { primary: "#4A6FA8", soft: "rgba(74,111,168,0.12)" },
  // Compat: phases antigas continuam mapeando enquanto o backend não re-seeda
  analysis:     { primary: "#FE7000", soft: "rgba(254,112,0,0.10)" },
  copy:         { primary: "#D86B5A", soft: "rgba(216,107,90,0.10)" },
  production:   { primary: "#A06FA8", soft: "rgba(160,111,168,0.12)" },
  optimization: { primary: "#6B7CA8", soft: "rgba(107,124,168,0.12)" },
};
